import { useEffect, useState } from 'react';
import { Check } from 'lucide-react';
import api from '@/lib/api';
import { SeoScoreRing } from '@/components/channel/SeoScoreRing';

function scoreBand(score) {
  const n = Number(score) || 0;
  if (n >= 80) return 'Strong';
  if (n >= 50) return 'Fair';
  return 'Needs work';
}

export function SeoChecklist({
  title = '',
  description = '',
  tags = '',
  hasThumbnail = false,
  language = '',
  categoryId = '',
  inPlaylist = false,
  privacy = 'public',
  onImprove,
  improving = false,
  className = '',
}) {
  const [result, setResult] = useState(null);

  useEffect(() => {
    const handle = setTimeout(async () => {
      const tagList = String(tags || '')
        .split(/[,#]+/)
        .map((tag) => tag.trim())
        .filter(Boolean);
      try {
        const { data } = await api.post('/channel/seo-score', {
          title,
          description,
          tags: tagList,
          has_custom_thumbnail: Boolean(hasThumbnail),
          default_language: language || null,
          category_id: categoryId || null,
          in_playlist: Boolean(inPlaylist),
          privacy_status: privacy || 'public',
        });
        setResult(data);
      } catch {
        setResult(null);
      }
    }, 350);
    return () => clearTimeout(handle);
  }, [title, description, tags, hasThumbnail, language, categoryId, inPlaylist, privacy]);

  const checklist = Array.isArray(result?.checklist) ? result.checklist : [];
  const passed = checklist.filter((item) => item.passed);
  const band = result ? scoreBand(result.score) : 'Checking';
  const phrase = String(result?.main_phrase || '').trim();

  return (
    <section className={`rounded-[18px] bg-[#1c2433] px-5 py-5 space-y-4 ${className}`.trim()}>
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-white">SEO checklist</p>
          <p className="text-sm text-[#8b95a7] mt-0.5">{band}</p>
          <p className="text-xs text-[#6d7788] mt-1">
            {result ? `${passed.length} of ${checklist.length} done` : 'Scoring this video…'}
          </p>
        </div>
        {result ? (
          <SeoScoreRing score={result.score} size={88} onImprove={onImprove} improving={improving} />
        ) : null}
      </div>
      <p className="text-xs text-[#7d8799]">
        {phrase ? `Checking the phrase “${phrase}”.` : 'Add tags. The first tag is the phrase this score looks for.'}
      </p>
      <ul className="space-y-2">
        {checklist.map((item) => {
          const earned = Number(item.points) || 0;
          const max = Number(item.max_points) || 0;
          const done = Boolean(item.passed);
          return (
            <li
              key={item.id}
              className={`flex items-start gap-3 rounded-2xl px-3.5 py-3 transition-colors ${
                done ? 'bg-[#34d399]/10' : 'bg-black/25'
              }`}
            >
              <span
                className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition-colors ${
                  done
                    ? 'border-[#34d399] bg-[#34d399] text-[#06211b]'
                    : earned > 0
                      ? 'border-[#d6ff4a]/70 text-[#d6ff4a]'
                      : 'border-white/20 text-transparent'
                }`}
                aria-hidden
              >
                <Check className="w-3 h-3" strokeWidth={3} />
              </span>
              <span className="min-w-0 flex-1">
                <span className={`block text-sm ${done ? 'text-[#d7f8ec]' : 'text-white'}`}>{item.label}</span>
                {!done && (item.tip || item.detail) ? (
                  <span className="block text-[11px] text-[#8b95a7] mt-1">{item.tip || item.detail}</span>
                ) : null}
              </span>
              <span className={`shrink-0 text-xs font-semibold tabular-nums ${done ? 'text-[#34d399]' : 'text-[#d6ff4a]'}`}>
                {done ? 'Done' : `+${Math.max(0, max - earned)}`}
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
